import axios from 'axios';
import { getToken, logout } from './auth';

const API_BASE_URL = 'https://api.nisa-care.com/api/v1';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      logout();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ========================================
// ADMIN AUTH APIs
// ========================================

export const authAPI = {
  login: (email, password) => api.post('/admin/auth/login', { email, password }),
  refreshToken: (refreshToken) => api.post('/admin/auth/refresh-token', { refresh_token: refreshToken }),
  logout: () => api.post('/admin/auth/logout'),
  getMe: () => api.get('/admin/auth/me'),
};

// ========================================
// ADMIN USER MANAGEMENT APIs (Manage other admins)
// ========================================

export const adminUsersAPI = {
  getAll: (params) => api.get('/admin/admin-users', { params }),
  create: (data) => api.post('/admin/admin-users', data),
  update: (id, data) => api.put(`/admin/admin-users/${id}`, data),
  delete: (id) => api.delete(`/admin/admin-users/${id}`),
};

// ========================================
// APP USERS MANAGEMENT APIs
// ========================================

export const usersAPI = {
  getAll: (params) => api.get('/admin/users', { params }),
  getById: (id) => api.get(`/admin/users/${id}`),
  suspend: (id) => api.put(`/admin/users/${id}/suspend`),
  restore: (id) => api.put(`/admin/users/${id}/restore`),
  deletePermanent: (id) => api.delete(`/admin/users/${id}/permanent`),
  getAnalytics: () => api.get('/admin/users/analytics'),
  exportUserData: (id) => api.get(`/admin/users/${id}/export`, { responseType: 'blob' }),
};

// ========================================
// USER DATA VIEWER APIs
// ========================================

export const userDataAPI = {
  getOverview: (userId) => api.get(`/admin/user-data/${userId}/overview`),
  getDailyLogs: (userId, params) => api.get(`/admin/user-data/${userId}/daily-logs`, { params }),
  getHabits: (userId) => api.get(`/admin/user-data/${userId}/habits`),
  getNotes: (userId) => api.get(`/admin/user-data/${userId}/notes`),
  getPrayers: (userId, params) => api.get(`/admin/user-data/${userId}/prayers`, { params }),
  getQada: (userId) => api.get(`/admin/user-data/${userId}/qada`),
  getTasbih: (userId) => api.get(`/admin/user-data/${userId}/tasbih`),
};

// ========================================
// GUIDANCE ARTICLES MANAGEMENT APIs
// ========================================

export const articlesAPI = {
  getAll: (params) => api.get('/admin/guidance/articles', { params }),
  getById: (id) => api.get(`/admin/guidance/articles/${id}`),
  create: (data) => api.post('/admin/guidance/articles', data),
  update: (id, data) => api.put(`/admin/guidance/articles/${id}`, data),
  delete: (id) => api.delete(`/admin/guidance/articles/${id}`),
  restore: (id) => api.put(`/admin/guidance/articles/${id}/restore`),
  publish: (id) => api.post(`/admin/guidance/articles/${id}/publish`),
  unpublish: (id) => api.post(`/admin/guidance/articles/${id}/unpublish`),
  bulkDelete: (articleIds) => api.post('/admin/guidance/articles/bulk-delete', { article_ids: articleIds }),
};

// ========================================
// DUAS MANAGEMENT APIs
// ========================================

export const duasAPI = {
  getAll: (params) => api.get('/admin/duas', { params }),
  create: (data) => api.post('/admin/duas', data),
  update: (id, data) => api.put(`/admin/duas/${id}`, data),
  delete: (id) => api.delete(`/admin/duas/${id}`),
  restore: (id) => api.put(`/admin/duas/${id}/restore`),
  getById: (id) => api.get(`/admin/duas/${id}`),
};

// ========================================
// CATEGORIES MANAGEMENT APIs
// ========================================

export const categoriesAPI = {
  // Guidance Categories
  getGuidanceCategories: () => api.get('/admin/categories/guidance'),
  createGuidanceCategory: (data) => api.post('/admin/categories/guidance', data),
  updateGuidanceCategory: (id, data) => api.put(`/admin/categories/guidance/${id}`, data),
  deleteGuidanceCategory: (id) => api.delete(`/admin/categories/guidance/${id}`),

  // Dua Categories
  getDuaCategories: () => api.get('/admin/categories/duas'),
  createDuaCategory: (data) => api.post('/admin/categories/duas', data),
  updateDuaCategory: (id, data) => api.put(`/admin/categories/duas/${id}`, data),
  deleteDuaCategory: (id) => api.delete(`/admin/categories/duas/${id}`),
};

// ========================================
// DAILY INSIGHTS MANAGEMENT APIs
// ========================================

export const insightsAPI = {
  getAll: (params) => api.get('/admin/insights/daily', { params }),
  getById: (id) => api.get(`/admin/insights/daily/${id}`),
  create: (data) => api.post('/admin/insights/daily', data),
  update: (id, data) => api.put(`/admin/insights/daily/${id}`, data),
  delete: (id) => api.delete(`/admin/insights/daily/${id}`),
  restore: (id) => api.put(`/admin/insights/daily/${id}/restore`),
  reorder: (insights) => api.post('/admin/insights/daily/reorder', { insights }),
};

// ========================================
// CONTACT MANAGEMENT APIs
// ========================================

export const contactAPI = {
  get: () => api.get('/admin/contact'),
  create: (data) => api.post('/admin/contact', data),
  update: (id, data) => api.put(`/admin/contact/${id}`, data),
};

// ========================================
// NOTIFICATIONS APIs
// ========================================

export const notificationsAPI = {
  broadcast: (data) => api.post('/admin/notifications/broadcast', data),
  getBroadcasts: (params) => api.get('/admin/notifications/broadcasts', { params }),
  getUserNotifications: (params) => api.get('/admin/notifications/user-notifications', { params }),
  getTemplates: () => api.get('/admin/notifications/templates'),
  createTemplate: (data) => api.post('/admin/notifications/templates', data),
  updateTemplate: (id, data) => api.put(`/admin/notifications/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/admin/notifications/templates/${id}`),
  clearBroadcasts: () => api.delete('/admin/notifications/broadcasts/clear'),
};

// ========================================
// MEDIA MANAGEMENT APIs
// ========================================

export const mediaAPI = {
  upload: (formData) => api.post('/admin/media/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getAll: (params) => api.get('/admin/media', { params }),
  delete: (id) => api.delete(`/admin/media/${id}`),
  getStats: () => api.get('/admin/media/stats'),
};

// ========================================
// SYSTEM MANAGEMENT APIs
// ========================================

export const systemAPI = {
  getSettings: () => api.get('/admin/system/settings'),
  updateSetting: (key, value) => api.put(`/admin/system/settings/${key}`, { value }),
  createSetting: (data) => api.post('/admin/system/settings', data),
  getSystemInfo: () => api.get('/admin/system/info'),
  toggleMaintenanceMode: (enabled) => api.post('/admin/system/maintenance-mode', { enabled }),
};

// ========================================
// SESSION MANAGEMENT APIs
// ========================================

export const sessionsAPI = {
  getUserSessions: (params) => api.get('/admin/sessions/user-sessions', { params }),
  getAdminSessions: (params) => api.get('/admin/sessions/admin-sessions', { params }),
  revokeUserSession: (id) => api.delete(`/admin/sessions/user-sessions/${id}`),
  revokeAllUserSessions: (userId) => api.delete(`/admin/sessions/user-sessions/user/${userId}/all`),
  revokeAdminSession: (id) => api.delete(`/admin/sessions/admin-sessions/${id}`),
  getStats: () => api.get('/admin/sessions/stats'),
};

// ========================================
// ANALYTICS APIs
// ========================================

export const analyticsAPI = {
  getDashboard: () => api.get('/admin/analytics/dashboard'),
  getUsers: (params) => api.get('/admin/analytics/users', { params }),
  getContent: () => api.get('/admin/analytics/content'),
  getEngagement: (params) => api.get('/admin/analytics/engagement', { params }),
  getActivityLog: (params) => api.get('/admin/analytics/activity-log', { params }),
  clearActivityLog: () => api.delete('/admin/analytics/activity-log/clear'),
  exportAnalytics: (startDate, endDate) => api.get(`/admin/analytics/export?start_date=${startDate}&end_date=${endDate}`, { responseType: 'blob' }),
};

export default api;